import type { ReactNode } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { Icon } from '../Icon/Icon';
import type { PressGesture } from '../primitives/Chip';
import { webTapFloor } from '../theme';
import type { useTheme } from './theme';
import { MarqueeText } from './MarqueeText';

// Text that IS a button: a bordered field with a chevron (#308, #318).
// The caller sets the width and puts the full text in the accessible name.
export function TitleButton({
  text,
  onPress,
  accessibilityLabel,
  accessibilityHint,
  expanded,
  gesture,
  hitSlop,
  style,
  renderChevron,
  fit = false,
  theme,
}: {
  text: string;
  onPress: () => void;
  accessibilityLabel: string;
  accessibilityHint?: string;
  /** A disclosure: announced as expanded, and the chevron turns up. */
  expanded?: boolean;
  gesture?: PressGesture;
  hitSlop?: PressableProps['hitSlop'];
  style?: StyleProp<ViewStyle>;
  /** Wraps the chevron only, the seam <UnseenCue> uses (as IconButton's
   *  renderGlyph). */
  renderChevron?: (chevron: ReactNode) => ReactNode;
  /** As wide as a short label (#318 «Info»): one line, no marquee. */
  fit?: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  const chevron = (
    <Icon
      name={expanded ? 'chevron-up' : 'chevron-down'}
      size={theme.typography.sizes.md}
      color={theme.palette.ink2}
    />
  );
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      // RNW drops accessibilityState, so the state also rides aria-* (as
      // IconButton does).
      {...(expanded === undefined
        ? {}
        : { accessibilityState: { expanded }, 'aria-expanded': expanded })}
      onPress={onPress}
      {...gesture}
      hitSlop={hitSlop}
      style={({ pressed }) => [styles.field, pressed && styles.pressed, style]}
    >
      {fit ? (
        <Text style={styles.title} numberOfLines={1}>
          {text}
        </Text>
      ) : (
        <MarqueeText text={text} style={styles.title} />
      )}
      {renderChevron ? renderChevron(chevron) : chevron}
    </Pressable>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // borderStrong, not border: on the card's surface `border` is ~1.1:1 and
    // the field would not be there (DS-37).
    field: {
      // RNW ignores hitSlop, so on the web the box itself reaches 44.
      minHeight: webTapFloor ?? 36,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      paddingHorizontal: s.s3,
      borderWidth: 1,
      borderColor: p.borderStrong,
      borderRadius: r.r2,
      backgroundColor: p.surface,
    },
    pressed: { backgroundColor: p.surface2 },
    title: {
      color: p.ink,
      fontSize: ty.sizes.base,
      lineHeight: Math.round(ty.sizes.base * ty.leading.snug),
      fontWeight: '700',
    },
  });
}
